// eslint-disable-next-line jira/filename-case, jira/restricted/react-component-props
import React, { forwardRef, type ComponentProps } from 'react';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import BreadcrumbsComponent from '@atlaskit/breadcrumbs/breadcrumbs';
import { BreadcrumbsItem } from '@atlaskit/breadcrumbs/breadcrumbs-item';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { Link, type MatchParams, type Query, type Route } from '@atlassian/react-resource-router';
import type { Breadcrumb } from '../model/types.tsx';
import BreadCrumbItemWithExtraAnalytics from './breadcrumb-item/view.tsx';

const breadcrumbsLabel = defineMessage({
	id: 'navigation-apps.breadcrumbs.aria-label',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Empty Breadcrumbs item',
	description: 'An empty breadcrumb, to avoid visual jumping',
});

type Props = {
	isVisible: boolean;
	items: Breadcrumb[];
};

export const AnchorLink: React.ForwardRefExoticComponent<
	Omit<
		React.AnchorHTMLAttributes<HTMLAnchorElement> & {
			children: React.ReactNode;
			target?: '_blank' | '_self' | '_parent' | '_top';
			href?: string;
			to?:
				| string
				| Route
				| Promise<
						| {
								default: Route;
						  }
						| Route
				  >;
			replace?: boolean;
			type?: 'a' | 'button';
			onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
			onMouseEnter?: (e: React.MouseEvent) => void;
			onMouseLeave?: (e: React.MouseEvent) => void;
			onPointerDown?: (e: PointerEvent) => void;
			params?: MatchParams;
			query?: Query;
			prefetch?: false | 'hover' | 'mount';
			state?: unknown;
		} & React.RefAttributes<HTMLAnchorElement | HTMLButtonElement>,
		'ref'
	> &
		React.RefAttributes<HTMLAnchorElement>
> = forwardRef<HTMLAnchorElement, ComponentProps<typeof Link>>((props, ref) => (
	<Link {...props} ref={ref} type="a">
		{props.children}
	</Link>
));

export default function Breadcrumbs(props: Props): React.JSX.Element | null {
	const { isVisible, items } = props;
	const { formatMessage } = useIntl();
	const totalItems = items.length;
	return isVisible ? (
		// onExpand is required prop in atlaskit's breadcrumbitem component
		<>
			<BreadcrumbsComponent onExpand={noop}>
				{isEmpty(items) ? (
					// This is needed in order to render an empty breadcrumb for SSR, to avoid visual jumping when javascript kicks in.
					<BreadcrumbsItem text="" aria-label={formatMessage(breadcrumbsLabel)} />
				) : (
					items.map((breadcrumb, index) => {
						const breadcrumbItem = (
							<BreadCrumbItemWithExtraAnalytics
								key={index}
								index={index}
								ariaCurrent={index + 1 === totalItems ? 'page' : undefined}
								hasSeparator
								text={breadcrumb.text}
								target={__SPA__ ? undefined : breadcrumb.target}
								href={breadcrumb.href}
								component={__SPA__ ? AnchorLink : undefined}
								legacyId={breadcrumb.legacyId}
								type={breadcrumb.type}
								analyticsAttributes={breadcrumb.analyticsAttributes}
								analyticsFields={breadcrumb.analyticsFields}
							/>
						);

						return breadcrumbItem;
					})
				)}
			</BreadcrumbsComponent>
		</>
	) : null;
}
